// What the start page announces (pages/news/announcements.ts) and what this
// device has seen: the announcements closed with ×, and the latest News post
// read (the News bell's dot). Kept on the device.
import { useEffect, useState } from "react";
import { ANNOUNCEMENTS, type Announcement } from "../pages/news/announcements";
import { NEWS, newsSlug } from "../pages/news/news";
import { DEMO } from "./design-flags";

const SEEN_KEY = "ar.news.seen";       // the latest post read (its slug)
const CLOSED_KEY = "ar.announce.closed"; // announcement ids closed with ×

function read(key: string): string[] {
  try { const v = JSON.parse(localStorage.getItem(key) || "[]"); return Array.isArray(v) ? v : []; } catch { return []; }
}
function write(key: string, v: string[]) {
  try { localStorage.setItem(key, JSON.stringify(v.slice(-40))); } catch { /* no storage */ }
}
const subs = new Set<() => void>();
const changed = () => subs.forEach((f) => f());

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function latestNewsSlug(): string | null {
  return NEWS[0] ? newsSlug(NEWS[0]) : null;
}

// What to show now, or null when nothing is running or it was closed.
export function currentAnnouncement(): Announcement | null {
  const closed = DEMO ? [] : read(CLOSED_KEY);
  return ANNOUNCEMENTS.find((x) => (!x.until || x.until >= today()) && closed.indexOf(x.id) === -1) ?? null;
}

// The News bell's dot: the latest post not read yet.
export function newsUnread(): boolean {
  const slug = latestNewsSlug();
  return !!slug && read(SEEN_KEY).indexOf(slug) === -1;
}

export function markNewsSeen(slug?: string): void {
  const s = slug ?? latestNewsSlug();
  if (!s) return;
  const seen = read(SEEN_KEY);
  if (seen.indexOf(s) === -1) { write(SEEN_KEY, [...seen, s]); changed(); }
}

export function closeAnnouncement(id: string): void {
  const closed = read(CLOSED_KEY);
  if (closed.indexOf(id) === -1) write(CLOSED_KEY, [...closed, id]);
  changed();
}

export function useAnnouncement(): { shown: Announcement | null; unread: boolean } {
  const [, tick] = useState(0);
  useEffect(() => {
    const f = () => tick((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return { shown: currentAnnouncement(), unread: newsUnread() };
}
